import React from "react";
import { useTranslation } from "react-i18next";
import type { components } from "../../apis/gen";
import { BlockButton as UntypedBlockButton } from "../../inJoy-components/Button/BlockButton";
import { ResponsiveModal } from "../../inJoy-components/Modal/ResponsiveModal";
import { CountdownScreen } from "./CountdownScreen";
import { ExpiredScreen } from "./ExpiredScreen";
import { GameStartScreen } from "./GameStartScreen";
import { LandingScreen } from "./LandingScreen";
import { StickyCountdownBar } from "./StickyCountdownBar";
import { TeamSetupScreen } from "./TeamSetupScreen";
import { GuestAppendState } from "./types";
import { WelcomeWaitingScreen } from "./WelcomeWaitingScreen";

const BlockButton = UntypedBlockButton as any;

const imgPromo = "/img/event-page/game_info_thumbnail.png";

export interface GuestAppendViewProps {
	state: GuestAppendState;
	event: components["schemas"]["Event"] | null;
	loading: boolean;
	error: string | null;
	email: string;
	setEmail: (email: string) => void;
	emailError?: string | null;
	onEmailBlur?: () => void;
	onConfirm: () => void;
	isSubmitting: boolean;
	submitted: boolean;
	onRetry?: () => void;
	onCloseSuccess?: () => void;
	timeLeft?: string;
	gameStartLeft?: string;
	showLoginModal?: boolean;
	onCloseLoginModal?: () => void;
	onLogin?: () => void;
	jerseyIsLight?: boolean | null;
	setJerseyIsLight?: (value: boolean) => void;
	teamNameLight?: string;
	setTeamNameLight?: (value: string) => void;
	teamNameDark?: string;
	setTeamNameDark?: (value: string) => void;
	onConfirmTeamSetup?: () => void;
}

export const GuestAppendView: React.FC<GuestAppendViewProps> = ({
	state,
	event,
	loading,
	error,
	email,
	setEmail,
	emailError,
	onEmailBlur,
	onConfirm,
	isSubmitting,
	submitted,
	onRetry,
	onCloseSuccess,
	timeLeft = "2:00",
	gameStartLeft = "5:30",
	showLoginModal = false,
	onCloseLoginModal,
	onLogin,
	jerseyIsLight = null,
	setJerseyIsLight,
	teamNameLight = "",
	setTeamNameLight,
	teamNameDark = "",
	setTeamNameDark,
	onConfirmTeamSetup,
}) => {
	const [isStickyBarVisible, setIsStickyBarVisible] = React.useState(false);
	const { t } = useTranslation();

	const loginModal = (
		<ResponsiveModal
			isOpen={showLoginModal}
			onClose={() => onCloseLoginModal?.()}
		>
			<div className="flex w-full flex-col gap-2 items-center text-center">
				<img className="w-[240px]" src={imgPromo} alt="promo thumbnail" />
				<p className="w-full text-[20px] font-semibold leading-[1.2] tracking-[-0.5px] text-[#47515f]">
					{t("guest.login_prompt.title")}
				</p>
				<p className="w-full text-[16px] font-medium leading-[1.25] text-[#b1b1b1]">
					{t("guest.login_prompt.message")}
				</p>
			</div>
			<div className="flex w-full flex-col gap-[8px] items-start pt-[16px]">
				<BlockButton
					type="Primary"
					size="Large"
					width="100%"
					onClick={() => onLogin?.()}
				>
					{t("guest.login_prompt.login")}
				</BlockButton>
				<BlockButton
					type="Secondary"
					size="Large"
					width="100%"
					onClick={() => onCloseLoginModal?.()}
				>
					{t("guest.login_prompt.later")}
				</BlockButton>
			</div>
		</ResponsiveModal>
	);

	if (loading) {
		return (
			<>
				<div className="flex items-center justify-center min-h-screen bg-white">
					<p className="Subtitle1">{t("common.loading_event")}</p>
				</div>
				{loginModal}
			</>
		);
	}

	if (state === GuestAppendState.EXPIRED || error) {
		return <ExpiredScreen />;
	}

	if (state === GuestAppendState.GAME_START) {
		return <GameStartScreen />;
	}

	if (state === GuestAppendState.COUNTDOWN) {
		return (
			<CountdownScreen
				timeLeft={gameStartLeft}
				teamNameLight={teamNameLight}
				teamNameDark={teamNameDark}
			/>
		);
	}

	if (state === GuestAppendState.WAITING) {
		return <WelcomeWaitingScreen />;
	}

	if (state === GuestAppendState.TEAM_SETUP) {
		return (
			<TeamSetupScreen
				jerseyIsLight={jerseyIsLight}
				onJerseyChange={(v) => setJerseyIsLight?.(v)}
				teamNameLight={teamNameLight}
				onTeamNameLightChange={(v) => setTeamNameLight?.(v)}
				teamNameDark={teamNameDark}
				onTeamNameDarkChange={(v) => setTeamNameDark?.(v)}
				onConfirm={() => onConfirmTeamSetup?.()}
				isSubmitting={isSubmitting}
			/>
		);
	}

	// Show sticky bar only when NOT in view of the main countdown (scrolled past)
	// AND we are in the Landing state.
	const showSticky = state === GuestAppendState.LANDING && isStickyBarVisible;

	return (
		<>
			<StickyCountdownBar
				isVisible={showSticky}
				timeLeft={gameStartLeft}
				onPlayNow={() => {
					window.scrollTo({ top: 0, behavior: "smooth" });
				}}
			/>
			<LandingScreen
				email={email}
				setEmail={setEmail}
				emailError={emailError}
				onEmailBlur={onEmailBlur}
				onConfirm={onConfirm}
				isSubmitting={isSubmitting}
				timeLeft={gameStartLeft}
				tokenTimeLeft={timeLeft}
				onCountdownInView={(inView) => {
					setIsStickyBarVisible(!inView);
				}}
			/>
			{loginModal}
		</>
	);
};
